import { Input } from "@/components/ui/input/text";
import useOutsideAlert from "@/lib/hooks/useOutsideAlert";
import { getFilteredData } from "@/lib/utils/data";
import classNames from "classnames";
import { ChangeEvent, FC, Fragment, Suspense, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { RiErrorWarningLine } from "react-icons/ri";
import { Loader } from "../../loader";
import MenuItem from "./list-item";
import { DropdownListDataType, DropdownListType } from "./types";

const DropdownList: FC<DropdownListType> = ({
  data,
  className,
  closeOnSelect,
  search,
  checkbox,
  searchPlaceholder,
  onClose,
  menuClassName,
  iconClassName,
  onSelect,
  value,
  highlightSelected,
  parentRef,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState<string>("");
  const [isScrolled, setIsScrolled] = useState(false);

  const handleClose = () => {
    if (onClose) {
      onClose();
    }
  };

  const handleScroll = () => {
    if (dropdownRef.current) {
      setIsScrolled(dropdownRef.current.scrollTop > 0);
    }
  };

  const handleSelect = (option: DropdownListDataType) => {
    if (onSelect) {
      onSelect(option);
    }
    if (closeOnSelect) {
      handleClose();
    }
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    try {
      setSearchValue(e?.target?.value);
    } catch (error) {
      console.error(error);
    }
  };

  const filteredMenu = getFilteredData({
    data: data,
    compareValue: searchValue,
    compareParams: ["title"],
  });

  const finalData = Boolean(searchValue) ? filteredMenu : data;

  useOutsideAlert(parentRef, handleClose);

  return (
    <div
      ref={dropdownRef}
      role="menu"
      className={classNames(
        "dropdown-menu relative bg-white text-font shadow-xl w-full max-h-[300px] overflow-auto focus:outline-0",
        className
      )}
      aria-orientation="vertical"
      aria-labelledby="menu-button"
      tabIndex={-1}
      onClick={(e) => e?.stopPropagation()}
      onScroll={handleScroll}
    >
      {search && (
        <div
          className={classNames(
            "w-full mx-auto sticky -top-2 bg-white pt-6 pb-3 px-4 z-10",
            isScrolled && "shadow"
          )}
        >
          <Input
            type="text"
            placeholder={searchPlaceholder ?? "Search"}
            onChange={handleSearch}
            value={searchValue}
            wrapperClassName="rounded-full"
            icon={<FiSearch size={6} />}
          />
        </div>
      )}

      <div className={classNames("w-full", search && "px-4")}>
        {Array.isArray(finalData) && finalData?.length > 0 ? (
          finalData?.map((menu, index) => {
            return (
              <Fragment key={index}>
                <Suspense fallback={<Loader />}>
                  <MenuItem
                    menu={menu}
                    className={menuClassName}
                    checkbox={checkbox}
                    iconClassName={iconClassName}
                    onSelect={handleSelect}
                    value={value}
                    highlightSelected={highlightSelected}
                  />
                </Suspense>
              </Fragment>
            );
          })
        ) : (
          <div className="flex items-center gap-1 text-red-600 my-3 px-3">
            <RiErrorWarningLine />
            <span className="text-sm">No Options Available</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DropdownList;
